import type { ReactNode } from 'react';

type IconProps = {
  name: string;
  className?: string;
};

type EmptyStateProps = {
  icon: string;
  title: string;
  message: string;
};

type LoadingStateProps = {
  label: string;
};

type ErrorStateProps = {
  message: string;
};

type StatusTone = 'ok' | 'rev' | 'risk' | 'new' | 'warn' | 'sub';

export function Icon({ name, className }: IconProps) {
  return <span className={className ? `material-symbols-rounded ${className}` : 'material-symbols-rounded'}>{name}</span>;
}

export function LoadingState({ label }: LoadingStateProps) {
  return (
    <div className="card">
      <div className="cbd loading-state">
        <span className="spinner" aria-hidden="true" />
        <div className="loading-copy">
          <div className="eyebrow">{label}</div>
          <div className="sk loading-line" />
        </div>
      </div>
    </div>
  );
}

export function ErrorState({ message }: ErrorStateProps) {
  return (
    <div className="alert risk" role="alert">
      <Icon name="error" />
      <div>{message}</div>
    </div>
  );
}

export function EmptyState({ icon, title, message }: EmptyStateProps) {
  return (
    <div className="card">
      <div className="empty">
        <div className="ic">
          <Icon name={icon} />
        </div>
        <b>{title}</b>
        <span>{message}</span>
      </div>
    </div>
  );
}

export function StatusChip({ value, tone }: { value: unknown; tone?: StatusTone }) {
  return (
    <span className={`st ${tone ?? statusTone(value)}`}>
      <span className="dot" />
      {formatValue(value)}
    </span>
  );
}

export function KeyValueCard({
  icon,
  title,
  rows,
}: {
  icon: string;
  title: string;
  rows: Array<[string, ReactNode]>;
}) {
  return (
    <div className="card">
      <div className="ch">
        <Icon name={icon} />
        <b>{title}</b>
      </div>
      <div className="cbd">
        {rows.map(([key, value]) => (
          <div className="kv" key={key}>
            <span className="k">{formatLabel(key)}</span>
            <span className="v">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function formatLabel(value: string) {
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatValue(value: unknown): string {
  if (value === null || typeof value === 'undefined' || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Active' : 'Disabled';
  if (typeof value === 'number') return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(2);
  if (Array.isArray(value)) return value.map(formatValue).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function statusTone(value: unknown): StatusTone {
  const normalized = String(value ?? '').toLowerCase();
  if (['allow', 'allowed', 'ok', 'delivered', 'active', 'enabled', 'won', 'valid', 'validated', 'match'].includes(normalized)) {
    return 'ok';
  }
  if (['review', 'pending', 'in review'].includes(normalized)) return 'rev';
  if (['deny', 'denied', 'failed', 'fail', 'lost', 'blocked', 'rejected'].includes(normalized)) return 'risk';
  if (['disabled', 'suspended', 'paused'].includes(normalized)) return 'warn';
  if (['submitted', 'queued'].includes(normalized)) return 'sub';
  return 'new';
}
